import { useEffect, useState } from 'react';
import { Button, Form, Offcanvas, Tab, Tabs } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ProcessTemplateTaskType, TaskTemplateCardType, TaskTemplateType } from '@econtractor/types';
import { useToast } from 'context/ToastContext';
import { createProcessRun, updateProcessTemplate } from 'api/apiTask';
import Paginator from 'components/others/Paginator';
import Editor from 'components/editors/Editor';
import SolarIcon from 'components/icons/SolarIcon';
import spinnerTitle from 'components/others/spinnerTitle';
import SpinnerBlock from 'components/others/SpinnerBlock';
import NewTaskTemplatePicker from '../../NewTask/NewTaskTemplatePicker';
import { useTaskDirList } from '../../TaskDir/model/useTaskDirList';
import OffTaskDir from '../../TaskDir/OffTaskDir';
import ProcessCategorySelect from '../ProcessCategory/ProcessCategorySelect';
import { appendProcessTask, removeProcessTask, replaceProcessTask } from '../model/processTaskSnapshot';
import ProcessDiagram from '../processDiagram';
import { useOffProcessDir } from './model/useOffProcessDir';
import ProcessTasksTable from './ProcessTasksTable';

const PaginatorAny = Paginator as any;

interface OffProcessDirProps {
    show: boolean;
    templateId: number | null;
    onHide: () => void;
    onSaved: () => void;
}

type StepEditorState =
    | { mode: 'create' }
    | { mode: 'edit'; task: ProcessTemplateTaskType }
    | null;

const OffProcessDir = ({ show, templateId, onHide, onSaved }: OffProcessDirProps) => {
    const { t } = useTranslation(['Tasks', 'common']);
    const { showToast } = useToast();
    const navigate = useNavigate();
    const form = useOffProcessDir({ show, templateId, onHide, onSaved });
    const [stepEditor, setStepEditor] = useState<StepEditorState>(null);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [diagramOpen, setDiagramOpen] = useState(false);
    const [starting, setStarting] = useState(false);
    const picker = useTaskDirList(pickerOpen && show);

    useEffect(() => {
        if (show) return;
        setStepEditor(null);
        setPickerOpen(false);
        setDiagramOpen(false);
        setStarting(false);
    }, [show]);

    const closeNested = () => {
        setStepEditor(null);
        setPickerOpen(false);
    };

    const handleHide = () => {
        closeNested();
        onHide();
    };

    const handleStepSaved = (template?: TaskTemplateCardType) => {
        if (!template) return;
        const next =
            !stepEditor || stepEditor.mode === 'create'
                ? appendProcessTask(form.tasks, template, true)
                : replaceProcessTask(form.tasks, stepEditor.task.dtId, template);
        form.setTasks(next);
        void form.persistTasks(next);
    };

    const handlePick = (template: TaskTemplateType) => {
        form.setTasks((prev) => appendProcessTask(prev, template, false));
        setPickerOpen(false);
    };

    const handleStart = async () => {
        const id = form.id;
        if (!id) return;
        const trimmed = form.name.trim();
        if (!trimmed) {
            showToast(t('processDir.errEmptyName'), 'danger');
            return;
        }
        setStarting(true);
        const saved = await updateProcessTemplate(id, {
            name: trimmed,
            description: form.description,
            categoryId: form.categoryId,
            tasks: form.tasks,
        });
        if (!saved?.stat) {
            setStarting(false);
            showToast(saved?.errCode === 'INVALID_TASKS' ? t('processDir.errTasks') : t('common:messError'), 'danger');
            return;
        }
        const result = await createProcessRun(id);
        setStarting(false);
        if (!result?.stat || !result.processId) {
            showToast(t('common:messError'), 'danger');
            return;
        }
        onSaved();
        onHide();
        navigate(`/tasks/processes?open=${result.processId}`);
    };

    return (
        <>
            <Offcanvas
                show={show}
                onHide={handleHide}
                placement="end"
                backdrop
                backdropClassName="task-dir-offcanvas-backdrop"
                className="process-offcanvas"
            >
                <Offcanvas.Header closeButton className="border-bottom">
                    <Offcanvas.Title>
                        {form.id ? t('processDir.titleEdit') : t('processDir.titleNew')}
                    </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                    <div className="off-task-dir-form d-flex flex-column h-100">
                        <div className="off-task-dir-content px-3 position-relative">
                            <SpinnerBlock show={form.loading || form.saving} onlyBlur={form.saving} />
                            <Tabs
                                variant="underline"
                                activeKey={form.activeTab}
                                onSelect={(tab) => tab && form.setActiveTab(tab)}
                                className="mb-3 invoice-tab border-bottom"
                            >
                                <Tab
                                    eventKey="general"
                                    title={
                                        <>
                                            <SolarIcon name="gear" className="me-2 fs-14" />
                                            {t('tabGeneral')}
                                        </>
                                    }
                                >
                                    <Form.Group className="mb-3">
                                        <label className="form-label text-muted">{t('fields.category')}</label>
                                        <ProcessCategorySelect
                                            value={form.categoryId}
                                            disabled={form.saving}
                                            onChange={(opt) => form.setCategoryId(opt?.value ?? null)}
                                        />
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <label className="form-label text-muted">{t('processDir.labelName')}</label>
                                        <Form.Control
                                            value={form.name}
                                            disabled={form.saving}
                                            autoFocus
                                            placeholder={t('processDir.placeholderName')}
                                            onChange={(e) => form.setName(e.target.value)}
                                        />
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        {form.showDescription ? (
                                            <>
                                                <label className="form-label text-muted">{t('labelInfo')}</label>
                                                <Editor
                                                    value={form.description || ''}
                                                    onChange={(next: string) => form.setDescription(next)}
                                                />
                                            </>
                                        ) : (
                                            <button
                                                type="button"
                                                className="btn btn-link p-0 align-baseline"
                                                onClick={() => form.setShowDescription(true)}
                                            >
                                                <SolarIcon name="plus" className="me-1" />
                                                {t('btnAddInfo')}
                                            </button>
                                        )}
                                    </Form.Group>
                                </Tab>  
                                <Tab
                                    eventKey="tasks"
                                    title={
                                        <>
                                            <SolarIcon name="list-ul" className="me-2 fs-14" />
                                            {t('processDir.tabTasks')}
                                        </>
                                    }
                                >
                                    <div className="d-flex gap-2 mb-3">
                                        <Button
                                            size="sm"
                                            variant="primary"
                                            disabled={form.saving}
                                            onClick={() => setPickerOpen(true)}
                                        >
                                            <SolarIcon name="folder2-open" className="fs-14 me-1" />
                                            {t('processDir.btnFromDir')}
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="success"
                                            disabled={form.saving}
                                            onClick={() => setStepEditor({ mode: 'create' })}
                                        >
                                            <SolarIcon name="plus" className="fs-14 me-1" />
                                            {t('processDir.btnNewTask')}
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="info"
                                            disabled={form.saving || !form.tasks.length}
                                            onClick={() => setDiagramOpen(true)}
                                        >
                                            <SolarIcon name="share" className="fs-14 me-1" />
                                            {t('processDiagram.open')}
                                        </Button>
                                    </div>
                                    <p className="text-muted fs-12">{t('processDir.hintGraph')}</p>
                                    <ProcessTasksTable 
                                        tasks={form.tasks}
                                        disabled={form.saving}
                                        onEdit={(task) => setStepEditor({ mode: 'edit', task })}
                                        onRemove={(task) => form.setTasks((prev) => removeProcessTask(prev, task))}
                                    />
                                </Tab>
                            </Tabs>
                        </div>
                        <div className="p-3 offcanvas-footer off-task-dir-footer">
                            <div className="d-flex justify-content-between">
                                {form.id ? (
                                    <Button
                                        variant="success"
                                        className="min100px"
                                        disabled={form.saving || form.loading || starting}
                                        onClick={() => void handleStart()}
                                    >
                                        {t('processDir.btnStart')}
                                    </Button>
                                ) : (
                                    <span />
                                )}
                                <Button
                                    variant="info"
                                    className="min100px"
                                    disabled={form.saving || form.loading}
                                    onClick={() => void form.handleSave()}
                                >
                                    {spinnerTitle(
                                        <>
                                            <SolarIcon name="check2-all" className="fs-14 me-2" />
                                            {t('common:buttons.save')}
                                        </>,
                                        form.saving,
                                        t('common:buttons.saving'),
                                    )}
                                </Button>
                            </div>
                        </div>
                    </div>
                </Offcanvas.Body>
            </Offcanvas>

            <Offcanvas
                show={pickerOpen}
                onHide={() => setPickerOpen(false)}
                placement="end"
                backdrop
                backdropClassName="process-step-offcanvas-backdrop"
                className="process-step-offcanvas"
            >
                <Offcanvas.Header closeButton className="border-bottom">
                    <Offcanvas.Title>{t('processDir.pickerTitle')}</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                    <div className="off-task-dir-form d-flex flex-column h-100">
                        <div className="off-task-dir-content px-3">
                            <NewTaskTemplatePicker
                                searchInput={picker.searchInput}
                                setSearchInput={picker.setSearchInput}
                                categoryId={picker.categoryId}
                                setFilterCategoryId={picker.setFilterCategoryId}
                                templates={picker.templates}
                                showSpinner={picker.showSpinner}
                                onSelect={handlePick}
                            />
                        </div>
                        {picker.paginator ? (
                            <div className="p-3 offcanvas-footer off-task-dir-footer">
                                <PaginatorAny
                                    paginator={picker.paginator}
                                    storageKey={picker.pageSizeKey}
                                    onChangePage={(nextPage: number) => picker.setPage(nextPage)}
                                    onChangeOnPage={(nextOnPage: number) => {
                                        picker.setOnPage(nextOnPage);
                                        picker.setPage(1);
                                    }}
                                />
                            </div>
                        ) : null}
                    </div>
                </Offcanvas.Body>
            </Offcanvas>

            <ProcessDiagram
                show={diagramOpen}
                tasks={form.tasks}
                onHide={() => setDiagramOpen(false)}
                onChange={form.setTasks}
            /> 

            <OffTaskDir 
                show={Boolean(stepEditor)}
                templateId={stepEditor?.mode === 'edit' ? stepEditor.task.templateId : null}
                forkFromTemplateId={
                    stepEditor?.mode === 'edit' && !stepEditor.task.owned ? stepEditor.task.templateId : null
                }
                disableRecurrence
                hideFromDirectory
                stacked
                onHide={() => setStepEditor(null)}
                onSaved={handleStepSaved}
            />
        </>
    );
};

export default OffProcessDir;
